import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  FileText, 
  CheckCircle, 
  FileCheck, 
  Scale, 
  Smartphone, 
  Clock,
  MapPin,
  Phone,
  Mail,
  CheckSquare,
  Zap,
  Shield,
  Award,
  Star,
  Quote
} from 'lucide-react';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const bookingLink = "https://calendar.app.google/FGd3pB8WQJ7MiKMe6";
  const phoneNumber = "639-554-3458";

  const services = [
    { icon: FileText, title: "Document Notarization", description: "Professional notarization of your important documents" },
    { icon: FileCheck, title: "Affidavits", description: "Sworn written statements for legal proceedings" },
    { icon: Scale, title: "Statutory Declarations", description: "Formal statements of fact under oath" },
    { icon: CheckCircle, title: "Oaths and Affirmations", description: "Administration of oaths for various purposes" },
    { icon: FileText, title: "Certified True Copies", description: "Official certification of document copies" },
    { icon: CheckSquare, title: "Signature Witnessing", description: "Professional witnessing of document signatures" },
    { icon: FileCheck, title: "Travel Consent Letters", description: "Notarization of completed travel consent forms" },
    { icon: Mail, title: "Invitation Letters", description: "Notarization of completed invitation letters" },
    { icon: Smartphone, title: "Mobile Notary Service", description: "We come to you for added convenience" },
    { icon: FileText, title: "Printing & Scanning", description: "Document printing and scanning support" }
  ];

  const trustReasons = [
    { icon: Zap, title: "Fast & Efficient Service", description: "Quick turnaround times without compromising quality" },
    { icon: MapPin, title: "Convenient Saskatoon Location", description: "Easy to access at 1303 Richardson Rd" },
    { icon: Smartphone, title: "Mobile Appointments Available", description: "We bring notary services to your location" },
    { icon: Shield, title: "Professional & Reliable", description: "Certified notary public you can trust" }
  ];

  const checklist = [
    "Bring valid government-issued photo ID",
    "Bring completed documents",
    "Do not sign before appointment",
    "Mobile appointments available",
    "No legal advice provided"
  ];

  const testimonials = [
    {
      name: "Sarah Thompson",
      role: "Real Estate Professional",
      content: "Quick and efficient service! Yogesh was professional and made the notarization process seamless. The mobile service was incredibly convenient for our busy schedule.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Business Owner",
      content: "Clear pricing, no surprises, and excellent service. I've used their services multiple times for various business documents. Highly recommend!",
      rating: 5
    },
    {
      name: "Jennifer Williams",
      role: "Parent",
      content: "Needed travel consent letters notarized for our family trip. Same-day appointment and very professional service. Made the whole process stress-free!",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "What documents do I need to bring?",
      answer: "You need to bring a valid government-issued photo ID (driver's license, passport, or similar) and your completed documents. Do not sign the documents before your appointment - signing must be done in the presence of the notary."
    },
    {
      question: "How long does a notarization appointment take?",
      answer: "Most notarization appointments take 10-15 minutes. Complex documents may take longer. We offer same-day appointments for urgent needs."
    },
    {
      question: "Do you offer mobile notary services?",
      answer: "Yes! We offer mobile notary services throughout Saskatoon and surrounding areas. Mobile service starts at $50 and includes travel to your location at a convenient time."
    },
    {
      question: "What are your operating hours?",
      answer: "We're open 7:00 AM to 11:00 PM, seven days a week. Book your appointment online to secure your preferred time slot."
    },
    {
      question: "Can you provide legal advice about my documents?",
      answer: "No. As a notary public, we cannot provide legal advice or draft legal documents. We can only notarize completed documents. If you need legal advice, please consult with a lawyer."
    },
    {
      question: "What forms of payment do you accept?",
      answer: "We accept cash, debit, and major credit cards. Payment is collected at the time of service, and pricing is confirmed before your appointment."
    },
    {
      question: "Do I need an appointment?",
      answer: "While walk-ins are welcome based on availability, we highly recommend booking an appointment online to guarantee your preferred time and avoid waiting."
    },
    {
      question: "Can you notarize documents in languages other than English?",
      answer: "Documents can be in any language, but you must understand the content of what you're signing. If needed, you may bring a translator to your appointment."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_23f98953-7d7d-4f05-9117-aa8b10eab538/artifacts/ryv0v8kj_LOGO%2026%2C%202026%2C%2012_03_19%20AM.png" 
                alt="Saskatoon Notary Public Services Logo" 
                className="h-14 sm:h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='80' viewBox='0 0 200 80'%3E%3Ctext x='10' y='40' font-family='Arial' font-size='18' fill='%23047857'%3ESaskatoon Notary%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>
            
            {/* Contact Info - Desktop */}
            <div className="hidden lg:flex items-center space-x-6 text-sm">
              <a href={`tel:${phoneNumber}`} className="flex items-center text-emerald-700 hover:text-emerald-800 font-semibold transition-colors">
                <Phone className="w-4 h-4 mr-2" />
                <span className="text-lg">{phoneNumber}</span>
              </a>
              <a href="mailto:info@sknotary.ca" className="flex items-center text-gray-700 hover:text-emerald-700 font-medium transition-colors">
                <Mail className="w-4 h-4 mr-2" />
                info@sknotary.ca
              </a>
            </div>

            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">Services</button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">Contact</button>
              <Button 
                onClick={() => window.open(bookingLink, '_blank')}
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-2 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Book Appointment
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          {/* Contact Info - Mobile (below header) */}
          <div className="lg:hidden border-t border-gray-200 py-2 flex flex-col sm:flex-row sm:justify-center sm:space-x-6 space-y-2 sm:space-y-0">
            <a href={`tel:${phoneNumber}`} className="flex items-center justify-center text-emerald-700 hover:text-emerald-800 font-semibold transition-colors">
              <Phone className="w-4 h-4 mr-2" />
              <span>{phoneNumber}</span>
            </a>
            <a href="mailto:info@sknotary.ca" className="flex items-center justify-center text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              <Mail className="w-4 h-4 mr-2" />
              info@sknotary.ca
            </a>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-3 border-t border-gray-200 pt-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-emerald-700 font-medium py-2">Services</button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left text-gray-700 hover:text-emerald-700 font-medium py-2">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-gray-700 hover:text-emerald-700 font-medium py-2">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-emerald-700 font-medium py-2">Contact</button>
              <Button 
                onClick={() => window.open(bookingLink, '_blank')}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                Book Appointment
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-emerald-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/7504780/pexels-photo-7504780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
            alt="Professional notary services" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Notary Public Services in Saskatoon
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 mb-4 leading-relaxed">
              Fast, professional notarization for affidavits, statutory declarations, certified copies, and more.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Serving Saskatoon, Warman, Martensville and surrounding areas
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center bg-white px-4 py-3 rounded-lg shadow-md">
                <CheckCircle className="w-5 h-5 text-emerald-700 mr-2" />
                <span className="text-gray-800 font-medium">Same-day appointments available</span>
              </div>
              <div className="flex items-center bg-white px-4 py-3 rounded-lg shadow-md">
                <Smartphone className="w-5 h-5 text-emerald-700 mr-2" />
                <span className="text-gray-800 font-medium">Mobile service available</span>
              </div>
              <div className="flex items-center bg-white px-4 py-3 rounded-lg shadow-md">
                <Award className="w-5 h-5 text-emerald-700 mr-2" />
                <span className="text-gray-800 font-medium">Clear, upfront pricing</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open(bookingLink, '_blank')}
                size="lg"
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Book Appointment
              </Button>
              <Button 
                onClick={() => window.open(`tel:${phoneNumber}`)}
                size="lg"
                variant="outline"
                className="border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Why Clients Choose Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustReasons.map((reason, index) => (
              <Card key={index} className="border border-gray-200 hover:border-emerald-700 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                    <reason.icon className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            Notary Services in Saskatoon
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            Professional notary public services tailored to your needs
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="bg-white border border-gray-200 hover:border-emerald-700 transition-all duration-300 hover:shadow-lg group">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-lg group-hover:bg-emerald-700 transition-colors duration-300">
                        <service.icon className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors duration-300" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            No hidden fees. Clear pricing you can trust.
          </p>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-700 shadow-xl mb-8">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">First Notarization</span>
                    <span className="text-2xl font-bold text-emerald-700">$25</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">Additional Document</span>
                    <span className="text-2xl font-bold text-emerald-700">$15</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">Certified True Copy</span>
                    <span className="text-2xl font-bold text-emerald-700">$10</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">Mobile Service</span>
                    <span className="text-2xl font-bold text-emerald-700">From $50</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">Printing</span>
                    <span className="text-2xl font-bold text-emerald-700">From $2/page</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                    <span className="text-lg font-semibold text-gray-900">Scanning</span>
                    <span className="text-2xl font-bold text-emerald-700">From $5/doc</span>
                  </div>
                </div>
              </div>
              <p className="text-center text-gray-600 mt-6 italic">
                Final pricing is confirmed before appointment.
              </p>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button 
              onClick={() => window.open(bookingLink, '_blank')}
              size="lg"
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Simple 3-Step Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-700 text-white rounded-full text-3xl font-bold mb-4 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Book Your Appointment</h3>
              <p className="text-gray-600">Choose a convenient time online and secure your spot instantly</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-700 text-white rounded-full text-3xl font-bold mb-4 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Bring ID & Documents</h3>
              <p className="text-gray-600">Come prepared with valid photo ID and your completed documents</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-700 text-white rounded-full text-3xl font-bold mb-4 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Notarized Quickly</h3>
              <p className="text-gray-600">Fast, professional service gets you on your way in no time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Book Your Appointment
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Choose a convenient time and secure your appointment instantly.
          </p>
          <Button 
            onClick={() => window.open(bookingLink, '_blank')}
            size="lg"
            className="bg-white text-emerald-700 hover:bg-gray-100 px-12 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            Book Appointment Now
          </Button>
          <p className="mt-6 text-emerald-100">
            Or call us directly at{' '}
            <a href={`tel:${phoneNumber}`} className="font-bold underline hover:text-white transition-colors">
              {phoneNumber}
            </a>
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            Trusted by hundreds of satisfied clients across Saskatoon
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-emerald-700 mb-3 opacity-50" />
                  <p className="text-gray-700 mb-4 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Before Your Visit
          </h2>
          <Card className="border-2 border-emerald-700 shadow-lg">
            <CardContent className="p-8">
              <ul className="space-y-4">
                {checklist.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-emerald-700 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Find answers to common questions about our notary services
          </p>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-700 py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <p className="text-gray-700 mb-4">Still have questions?</p>
            <Button 
              onClick={() => window.open(bookingLink, '_blank')}
              size="lg"
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Book a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Contact Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border border-gray-200 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Get In Touch</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Phone className="w-6 h-6 text-emerald-700 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <a href={`tel:${phoneNumber}`} className="text-emerald-700 hover:underline text-lg">
                        {phoneNumber}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="w-6 h-6 text-emerald-700 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <a href="mailto:info@sknotary.ca" className="text-emerald-700 hover:underline text-lg">
                        info@sknotary.ca
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-6 h-6 text-emerald-700 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Location</p>
                      <p className="text-gray-700 text-lg">1303 Richardson Rd</p>
                      <p className="text-gray-700 text-lg">Saskatoon, Saskatchewan</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Clock className="w-6 h-6 text-emerald-700 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Operating Hours</p>
                      <p className="text-gray-700 text-lg">7:00 AM - 11:00 PM</p>
                      <p className="text-gray-600">7 Days a Week</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-6 h-6 text-emerald-700 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Service Area</p>
                      <p className="text-gray-700 text-lg">Saskatoon & surrounding areas</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-lg bg-emerald-700 text-white">
              <CardContent className="p-8 flex flex-col justify-center h-full">
                <h3 className="text-2xl font-semibold mb-4">Ready to Get Started?</h3>
                <p className="text-emerald-50 mb-6 text-lg">
                  Book your appointment now and experience fast, professional notary services you can trust.
                </p>
                <Button 
                  onClick={() => window.open(bookingLink, '_blank')}
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-gray-100 w-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Book Appointment
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_23f98953-7d7d-4f05-9117-aa8b10eab538/artifacts/ryv0v8kj_LOGO%2026%2C%202026%2C%2012_03_19%20AM.png" 
                alt="Saskatoon Notary Public Services Logo" 
                className="h-28 w-auto mb-4 drop-shadow-lg"
              />
              <p className="text-gray-400 mb-2">Professional notary public services in Saskatoon</p>
              <p className="text-gray-400 text-sm">Serving Saskatoon, Warman, Martensville and surrounding areas</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="text-gray-400 hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-white transition-colors">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  <a href={`tel:${phoneNumber}`} className="hover:text-white transition-colors">{phoneNumber}</a>
                </li>
                <li className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  <a href="mailto:info@sknotary.ca" className="hover:text-white transition-colors">info@sknotary.ca</a>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                  <span>1303 Richardson Rd, Saskatoon, SK</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="text-center mb-4">
              <h3 className="text-xl font-semibold mb-2">Saskatoon Notary Public Services</h3>
              <p className="text-gray-400">Yogesh Kumar – Notary Public</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-400 text-center">
                <strong className="text-white">Important:</strong> Government-issued photo ID required. Documents must be complete before appointment. No legal advice provided.
              </p>
            </div>
            <p className="text-center text-gray-500 text-sm">
              © {new Date().getFullYear()} Saskatoon Notary Public Services. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky Book Button */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-200 p-4 shadow-lg z-40">
        <Button 
          onClick={() => window.open(bookingLink, '_blank')}
          className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-5 text-lg font-semibold shadow-lg min-h-[50px]"
        >
          Book Appointment
        </Button>
      </div>
    </div>
  );
};

export default Home;
